/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Router, applyRouterMiddleware, Route, IndexRoute, IndexRedirect } from 'react-router';
import Helmet from 'react-helmet';
import { useScroll } from 'react-router-scroll';
// eslint-disable-next-line no-unused-vars
import {
  userIsAuthenticated,
  userCan,
// userCanViewDashboard
} from 'components/Auth';
import Unauthorized from 'components/Unauthorized';
import 'normalize.css';
import 'styles/global.scss';
import Main from './screens/main';
import MainIndexRedirect from './screens/main/redirect.jsx';
import Employees, {
  EmployeeList,
  EmployeeNew,
  EmployeeDetails,
  EmployeeEdit,
} from './screens/main/screens/employees';
import Stores, {
  StoreList,
  StoreNew,
  StoreDetails,
  StoreEdit,
} from './screens/main/screens/stores';
import Estimators, {
  EstimatorList,
  EstimatorDetails,
  EstimatorNew,
  EstimatorEdit,
} from './screens/main/screens/estimators';
import IQMs, {
  IQMList,
  IQMDetails,
  IQMNew,
  IQMEdit,
} from './screens/main/screens/iqms';
import Installers, {
  InstallerList,
  InstallerNew,
  InstallerDetails,
  InstallerEdit,
} from './screens/main/screens/installers';
import Phases, {
  PhaseList,
  PhaseNew,
  PhaseEdit,
  PhaseShow,
  PhaseHistory,
  PhaseDetails,
  PhaseContract,
  PhaseDocuments,
  PhaseContractVerificationChecklist,
  PhaseSchedulingChecklist,
  PhaseAppointments,
  RelatedPhases,
  WarehouseMaterials,
  // PhaseSurvey,
  PhaseCommunications,
  PhaseMaterialsChecklist,
} from './screens/main/screens/phases';
import Workrooms, {
  WorkroomList,
  WorkroomDetails,
  WorkroomNew,
  WorkroomEdit,
} from './screens/main/screens/workrooms';
import Schedule from './screens/main/screens/schedule';
// import Dashboard from './screens/main/screens/dashboard';
import WarehouseReport from './screens/main/screens/reports/warehouse';
import InstallWorkorderReport from './screens/main/screens/reports/installWorkorderReport';
import JobHistoryReport from './screens/main/screens/reports/jobHistory';
import InstallerPaperwork from './screens/main/screens/reports/installerPaperwork';
import ActivityReport from './screens/main/screens/reports/activity';
import TagsReport from './screens/main/screens/reports/tags';
import Scraper from './screens/main/screens/tools/scraper';
import PricePatchUploads from './screens/main/screens/pricePatchUploads';
import LineItems from './screens/main/screens/lineItems';
import BulkUpdateUploads from './screens/main/screens/bulkUpdateUploads';
import MarginUploads from './screens/main/screens/marginUploads';
import ActivityUploads from './screens/main/screens/activityUploads';
import BulkProjectsLoad from './screens/main/screens/bulkProjectsLoad';
import LowesSurveyUploads from './screens/main/screens/lowesSurveyUploads';
import DefaultInstallerPay from './screens/main/screens/defaultInstallerPay';
import HolidayPromoSettings from './screens/main/screens/holidayPromoSettings';

import Login from './screens/login';
import NewPhaseAppointment from './screens/main/screens/phases/phaseShow/tabs/phaseAppointments/components/newPhaseAppointment';
import EditPhaseAppointment from './screens/main/screens/phases/phaseShow/tabs/phaseAppointments/components/editPhaseAppointment';
import IfiTemplatePage from './screens/main/screens/phases/phaseShow/tabs/ifiTemplate';

import RefigureForm from './screens/refigure/form';
import RefigureThankYou from './screens/refigure/thankyou';
import PrintLabel from './screens/printLabel';

const Authenticated = userIsAuthenticated(props => props.children);

const App = props => (
  <div>
    <Helmet
      titleTemplate="%s - Service Pros"
      defaultTitle="Service Pros"
    />
    <Router name="app" history={props.history} render={applyRouterMiddleware(useScroll())}>
      <Route component={Authenticated}>
        <Route name="main" path="/" component={Main}>
          <IndexRoute component={MainIndexRedirect} />
          <Route name="phases" path="/phases" component={userCan('view', 'phase', Phases)}>
            <IndexRoute name="phase-list" component={PhaseList} />
            <Route name="phase-add" path="new" component={userCan('create', 'phase', PhaseNew)} />
            <Route name="phase-show" path=":id" component={userCan('view', 'phase', PhaseShow)}>
              <IndexRedirect to="history" />
              <Route name="phase-history" path="history" component={userCan('view', 'event', PhaseHistory, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-info" path="info" component={userCan('view', 'phase_info', PhaseDetails, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-labor-product-accounting" path="labor-product-accounting(/:table)" component={userCan('view', ['accounting', 'labor'], PhaseContract, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-documents" path="documents" component={userCan('view', 'document', PhaseDocuments, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-cv-checklist" path="cv-checklist" component={userCan('view', 'labor', PhaseContractVerificationChecklist, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-scheduling-checklist" path="scheduling-checklist" component={userCan('manage', 'checklist', PhaseSchedulingChecklist, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-appointments" path="appointments" component={userCan('view', 'appointment', PhaseAppointments, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-new-appointment" path="appointments/new/:apptType" component={userCan('create', 'appointment', NewPhaseAppointment)} />
              <Route name="phase-edit-appointment" path="appointments/:appointmentId/edit/:apptType" component={userCan('update', 'appointment', EditPhaseAppointment)} />
              <Route name="related-phases" path="related-phases" component={userCan('view', 'related_phase', RelatedPhases, { redirectPath: 'labor-product-accounting' })} />
              <Route name="warehouse-materials" path="warehouse-materials" component={userCan('view', 'warehouse', WarehouseMaterials, { redirectPath: 'labor-product-accounting' })} />
              { /* <Route name="phase-survey" path="phase-survey" component={userCan('view', 'survey', PhaseSurvey, { redirectPath: 'info' })} /> */ }
              <Route name="phase-communications" path="communications" component={userCan('view', 'communications', PhaseCommunications, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-materials-checklist" path="materials-checklist" component={userCan('view', 'materials_checklist', PhaseMaterialsChecklist, { redirectPath: 'labor-product-accounting' })} />
              <Route name="phase-ifi-template" path="ifi-template" component={IfiTemplatePage} />
            </Route>
            <Route name="phase-edit" path=":id/edit" component={userCan('update', 'phase', PhaseEdit)} />
          </Route>
          {/* <Route name="dashboard" path="/dashboard" component={userCanViewDashboard(Dashboard)} /> */}
          <Route name="schedule" path="/schedule(/:year/:month/:day)" component={userCan('view', 'schedule', Schedule)} />
          {
            // TODO
          }
          <Route name="reports" path="/reports">
            <Route name="warehouse-report" path="warehouse" component={WarehouseReport} />
            <Route name="install-workorder-report" path="install-workorder" component={InstallWorkorderReport} />
            <Route name="job-history-report" path="job-history" component={JobHistoryReport} />
            <Route name="installer-paperwork" path="installer-paperwork" component={InstallerPaperwork} />
            <Route name="activity-report" path="activity" component={ActivityReport} />
            <Route name="tags-report" path="tags-report" component={TagsReport} />
          </Route>
          <Route name="tools" path="/tools">
            <Route name="scraper" path="scraper" component={Scraper} />
          </Route>
          <Route name="employees" path="/employees" component={userCan('view', 'employee', Employees)}>
            <IndexRoute name="employee-list" component={EmployeeList} />
            <Route name="employee-add" path="new" component={userCan('create', 'employee', EmployeeNew)} />
            <Route
              name="employee-edit"
              path=":id/edit"
              component={userCan('update', 'employee', EmployeeEdit)}
            />
            <Route name="employee-details" path=":id" component={userCan('view', 'employee', EmployeeDetails)} />
          </Route>
          <Route name="iqms" path="/iqms" component={userCan('view', 'iqm', IQMs)}>
            <IndexRoute name="iqm-list" component={IQMList} />
            <Route name="iqm-add" path="new" component={userCan('create', 'iqm', IQMNew)} />
            <Route
              name="iqm-edit"
              path=":id/edit"
              component={userCan('update', 'iqm', IQMEdit)}
            />
            <Route name="iqm-details" path=":id" component={userCan('view', 'iqm', IQMDetails)} />
          </Route>
          <Route name="stores" path="/stores" component={userCan('view', 'store', Stores)}>
            <IndexRoute name="store-list" component={StoreList} />
            <Route name="store-add" path="new" component={userCan('create', 'store', StoreNew)} />
            <Route name="store-details" path=":id" component={userCan('view', 'store', StoreDetails)} />
            <Route name="store-edit" path=":id/edit" component={userCan('update', 'store', StoreEdit)} />
          </Route>
          <Route name="estimators" path="/estimators" component={userCan('view', 'estimator', Estimators)}>
            <IndexRoute name="estimator-list" component={EstimatorList} />
            <Route name="estimator-add" path="new" component={userCan('create', 'estimator', EstimatorNew)} />
            <Route name="estimator-details" path=":id" component={userCan('view', 'estimator', EstimatorDetails)} />
            <Route name="estimator-edit" path=":id/edit" component={userCan('update', 'estimator', EstimatorEdit)} />
          </Route>
          <Route name="installers" path="/installers" component={userCan('view', 'installer', Installers)}>
            <IndexRoute name="installer-list" component={InstallerList} />
            <Route name="installer-add" path="new" component={userCan('create', 'installer', InstallerNew)} />
            <Route name="installer-details" path=":id" component={userCan('view', 'installer', InstallerDetails)} />
            <Route name="installer-edit" path=":id/edit" component={userCan('update_basic', 'installer', InstallerEdit)} />
          </Route>
          <Route name="workrooms" path="/workrooms" component={userCan('view', 'workroom', Workrooms)}>
            <IndexRoute name="workroom-list" component={WorkroomList} />
            <Route name="workroom-add" path="new" component={userCan('create', 'workroom', WorkroomNew)} />
            <Route name="workroom-details" path=":id" component={userCan('view', 'workroom', WorkroomDetails)} />
            <Route name="workroom-edit" path=":id/edit" component={userCan('update', 'workroom', WorkroomEdit)} />
          </Route>
          <Route name="pricePatchUploads" path="/pricepatchuploads" component={userCan('view', 'price_patch_upload', PricePatchUploads)} />
          <Route name="bulkUpdateUploads" path="/bulk-updates" component={userCan('view', 'bulk_update_upload', BulkUpdateUploads)} />
          <Route name="marginUploads" path="/margin-updates" component={userCan('view', 'margin_upload', MarginUploads)} />
          <Route name="activityUploads" path="/activity-updates" component={userCan('view', 'activity_upload', ActivityUploads)} />
          <Route name="bulkProjectsLoad" path="/bulk-projects" component={userCan('view', 'bulk_projects_load', BulkProjectsLoad)} />
          <Route name="lowesSurveyUploads" path="/lowes-survey" component={userCan('view', 'lowes_survey_upload', LowesSurveyUploads)} />
          <Route name="line-items" path="/line-items" component={userCan('view', 'line_item', LineItems)} />
          <Route name="defaultInstallerPay" path="/default-installer-pay" component={userCan('manage', 'default_installer_pay', DefaultInstallerPay)} />
          <Route name="holidayPromoSettings" path="/holiday-promo-settings" component={userCan('manage', 'holiday_promo_settings', HolidayPromoSettings)} />
        </Route>
      </Route>
      <Route name="login" path="/login" component={Login} />
      <Route name="refigure" path="/refigure" component={RefigureForm} />
      <Route name="refigure-thank-you" path="/refigure/thankyou" component={RefigureThankYou} />
      <Route name="print-label" path="/print-label" component={PrintLabel} />
      <Route name="unauthorized" path="/no-access" component={Unauthorized} />
      <Route path="*" component={Unauthorized} />
    </Router>
  </div>
);

App.propTypes = {
  history: PropTypes.shape({}),
};

export default App;



// WEBPACK FOOTER //
// ./client/app/app.jsx